import { UniversalLayout as ZDSUniversalLayout } from '@zapier/universal-layout';
import { ReactNode, useState } from 'react';
import { useRouter } from 'next/router';
import { CurrentAccountIdProvider } from '@zapier/identity';
import { useQuery, useMutation } from '@apollo/client';
import {
  EventAction,
  InteractionName,
  useAvroTracking,
  useTrackEvent,
} from '../../observability';
import { Alert, AlertType } from '@zapier/design-system';
import { RichTextHTMLString } from '../sections/types';
import { RichTextHtml } from '../RichTextHtml/RichTextHtml';

type Props = {
  alertBanner?: {
    title?: string;
    body?: RichTextHTMLString;
    type: AlertType;
    dismissable: boolean;
  };
  children: ReactNode;
  hideFooter?: boolean;
  hideHeader?: boolean;
  shouldRenderAlphabetNav?: boolean;
  shouldRenderSeoLinks?: boolean;
};

export const UniversalLayout = ({
  children,
  hideFooter = false,
  hideHeader = false,
  shouldRenderAlphabetNav = false,
  shouldRenderSeoLinks = false,
  alertBanner,
}: Props) => {
  const [showAlertBanner, setShowAlertBanner] = useState(true);

  const { emitUserInteractionEvent } = useAvroTracking();
  const trackEvent = useTrackEvent();
  const currentUrl = useRouter()?.pathname || '';

  const handleDismissAlertBanner = () => {
    setShowAlertBanner(false);
    emitUserInteractionEvent({
      event_action: EventAction.click,
      interaction_name: InteractionName.dismiss,
      option_selected: 'dismiss_button',
      custom_tracking_fields: { component: 'UniversalLayout' },
      ...(alertBanner?.title && {
        interaction_raw_text: alertBanner.title,
      }),
    });
  };

  return (
    <CurrentAccountIdProvider>
      <ZDSUniversalLayout
        currentUrl={currentUrl}
        renderAlertBanner={() =>
          showAlertBanner && !!alertBanner?.title && !!alertBanner?.type ? (
            <Alert
              layout="banner"
              title={alertBanner.title}
              type={alertBanner.type}
              {...(alertBanner.dismissable && {
                onDismiss: handleDismissAlertBanner,
              })}
            >
              {alertBanner.body && (
                <RichTextHtml
                  isDarkBackground={alertBanner.type !== 'warning'}
                  html={alertBanner.body}
                />
              )}
            </Alert>
          ) : null
        }
        renderFooter={hideFooter ? () => null : undefined}
        renderHeader={hideHeader ? () => null : undefined}
        shouldRenderAlphabetNav={shouldRenderAlphabetNav}
        shouldRenderSeoLinks={shouldRenderSeoLinks}
        // @ts-ignore: as suggested by Alej (https://github.com/zapier/blog/pull/375#discussion_r545454080)
        trackEvent={trackEvent}
        useMutation={useMutation}
        useQuery={useQuery}
      >
        {children}
      </ZDSUniversalLayout>
    </CurrentAccountIdProvider>
  );
};
